// src/com/dc_veh_ban_state/DcVehBanState.js
import React, { useEffect, useRef, useState } from 'react';
//import { styled } from '@mui/material/styles';
import { IconButton, CircularProgress } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import SearchBox from '../../common/SearchBox';
import {
    ContentConainer, ContentHeader, ControlBox, ToolBox
} from '../../common/TopViewControl';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import { authTableTreeRepo, vehBanningListRepo } from '../../value/AppModel';
import { LIST_VEH_BANNING, GET_VEH_BANNING } from '../veh_banning/VehBanningGql';
import {
    ADD_DC_VEH_BAN_STATE, EDIT_DC_VEH_BAN_STATE, REMOVE_DC_VEH_BAN_STATE
} from './DcVehBanStateGql';
import { DcVehBanStateDictionary } from './DcVehBanStateDict';
import { VehBanningDictionary } from '../veh_banning/VehBanningDict';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import FlexyTable from '../../common/flexytable/FlexyTable';
import DcVehBanStateInput from './DcVehBanStateInput';

/**
 * 배차금지 및 제한
 * (DC_VEH_BAN_STATE) 정보 View Controller
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-10-02 14:53:28 using DbdbTap
 */
export default function DcVehBanState({
    viewServiceTags // for switching view
}) {
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const [editorState, setEditorState] = useState(AppObject.EmptyViewerState);
    const vehBanningListData = useReactiveVar(vehBanningListRepo);
    const [searchText, setSearchText] = useState('');
    const openConfirmDialog = useConfirmDialog();
    const checkAuth = useAuthorizationCheck();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);
    const isEditingRef = useRef(false); // 플래그 생성

    // ##### Call GraphQL to get List #####
    // 목록은 view에서 가져옴.
    const [getVehBanningList, responseList] = useLazyQuery(LIST_VEH_BANNING, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: VehBanningDictionary.errorTitle.Get, message: [error.message]})
    });
    // 아이템은 view에서 가져옴.
    const [getVehBanningItem, responseItem] = useLazyQuery(GET_VEH_BANNING, {
		...AppObject.NoCachedFetch,
		onCompleted: (data, option) => {onCompleteGetItem(data, option)},
		onError: (error) => addAlert({title: VehBanningDictionary.errorTitle.Get, message: [error.message]})
	});
    
    // ##### GraphQL Mutation. 'onError' for prevent from printing error at console (Uncaught exception) ###
    const [addDcVehBanState, responseAdd] = useMutation( ADD_DC_VEH_BAN_STATE, {
		onCompleted: (data, option) => onCompleteAdd(data, option), 
		onError: (error) => addAlert({title: DcVehBanStateDictionary.errorTitle.Add, message: [error.message]})
	} );
    const [editDcVehBanState, responseEdit] = useMutation( EDIT_DC_VEH_BAN_STATE, {
		onCompleted: (data, option) => onCompleteEdit(data, option),
		onError: (error) => addAlert({title: DcVehBanStateDictionary.errorTitle.Edit, message: [error.message]})
	} );
    const [removeDcVehBanState, responseRemove] = useMutation( REMOVE_DC_VEH_BAN_STATE, {
		onCompleted: (data, option) => onCompleteRemove(data, option),
		onError: (error) => addAlert({title: DcVehBanStateDictionary.errorTitle.Remove, message: [error.message]})
	});

    const getVehBanningListNow = () => {
        getVehBanningList({variables:{comId:null}});
        lastReloadRef.current = new Date().getTime()/1000;
    };

    useEffect(() => {
        const diff = new Date().getTime()/1000 - vehBanningListData.when;
        if(diff > 300)
            getVehBanningListNow();
    }, []);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.vehBanningList) {
            vehBanningListRepo({when:new Date().getTime()/1000, records:data.vehBanningList});
        }
    };

    const onCompleteGetItem = (data, option) => {
        if(data.vehBanningItem) {
            setEditorState({open:true, item:data.vehBanningItem});
        }
        else addAlert({title:VehBanningDictionary.errorTitle.Get, message:['지정한 배차금지정보를 찾을 수 없습니다.']});
    };

    const onCompleteAdd = (data, clientOption) => {
        if(data.dcVehBanStateAdd.ok) {
            setEditorState(AppObject.EmptyViewerState);
            getVehBanningListNow();
        }
        else addAlert({title:DcVehBanStateDictionary.errorTitle.Add, message:[data.dcVehBanStateAdd.message]});
    };

    const onCompleteEdit = (data, clientOption) => {
        if(data.dcVehBanStateEdit.ok) {
            setEditorState(AppObject.EmptyViewerState);
            getVehBanningListNow();
        }
        else addAlert({title:DcVehBanStateDictionary.errorTitle.Edit, message:[data.dcVehBanStateEdit.message]});
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.dcVehBanStateRemove.ok) getVehBanningListNow();
        else addAlert({title:DcVehBanStateDictionary.errorTitle.Remove, message:[data.dcVehBanStateRemove.message]});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 3) {
            // Too short to reload
            enqueueSnackbar(AppWord.ReloadNotIn3Sec, {variant:'info', autoHideDuration:2000});
        }
        else getVehBanningListNow();
    };

    const onRequestSave = (item, isEdit) => {
        const mutateParam = {variables:{dcVehBanState:item}};
        if(isEdit) editDcVehBanState(mutateParam);
        else addDcVehBanState(mutateParam);
    };

    const onClickAdd = () => {
        setEditorState({open:true, item:null});
    };

    const onRequestEdit = (item) => {
        isEditingRef.current = true;
        getVehBanningItem({variables:{vehInfoSeq:item.vehInfoSeq}});
    };

    const confirmRemove = async (item) => {
        const confirm = await openConfirmDialog({
            title: "배차금지정보 삭제",
            content: [
                `배차금지정보(${item.vehPlates})를 삭제하시겠습니까?`,
                '삭제 후 복구할 수 없습니다. 계속하시겠습니까?'
            ]
        });

        if(confirm)
            removeDcVehBanState({variables:{dcVehBanState:{vehInfoSeq:item.vehInfoSeq}}});
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const onDoubleClickRow = (record) => {
        isEditingRef.current = false;
        getVehBanningItem({variables:{vehInfoSeq:record.vehInfoSeq}});
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.DELETE: confirmRemove(row); break;
            case AppObject.command.EDIT: onRequestEdit(row); break;
            default: return;
        }
    };

    const canAdd = checkAuth(authTableTree.tree.pr_dc_veh_ban_state_add);
    const canEdit = checkAuth(authTableTree.tree.pr_dc_veh_ban_state_edit);
    const canRemove = checkAuth(authTableTree.tree.pr_dc_veh_ban_state_remove);

    const tools = []; // VIEW는 없고 EDIT, DELETE 만 있음

    if(canEdit) tools.push({id:AppObject.command.EDIT, icon:<EditIcon/>});
    if(canRemove)
        tools.push({id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning});

    const loadingAny
    = responseEdit.loading ||
    responseAdd.loading ||
    responseRemove.loading ||
    responseItem.loading ||
    responseList.loading;

    return(
        <ContentConainer>
            {viewServiceTags}
            <ContentHeader>
                <ControlBox>
                    <SearchBox
                        value={searchText}
                        onChangeText={onChangeSearchText}
                    />
                </ControlBox>
                <ToolBox>
                    {
                        loadingAny ?
                        <CircularProgress size={20} />
                        :
                        <IconButton
                            color={AppStyle.mui.inherit}
                            onClick={onClickReload}
                        >
                            <CachedIcon />
                        </IconButton>
                    }
                    {
                        canAdd &&
                        <IconButton
                            color={AppStyle.mui.primary}
                            style={{marginLeft:10}}
                            onClick={onClickAdd}
                        >
                            <NoteAddIcon />
                        </IconButton>
                    }
                </ToolBox>
            </ContentHeader>
            <FlexyTable
                name={VehBanningDictionary.tableProps.tableId}
                tableProps={VehBanningDictionary.tableProps}
                records={vehBanningListData.records}
                search={searchText}
                tools={tools}
                onClickTool={onClickToolOnRow}
                onRowDoubleClicked={onDoubleClickRow}
            />
            <DcVehBanStateInput
                open={editorState.open}
                item={editorState.item}
                onClose={()=>setEditorState(AppObject.EmptyViewerState)}
                responseSaving={editorState.item ? responseEdit : responseAdd}
                onRequestSave={onRequestSave}
            />
        </ContentConainer>
    );
}
